import { queryString } from 'helpers';
import { AppContext } from 'providers/AppContext';
import { useContext, useEffect, useMemo, useState } from 'react';
import useSWR from 'swr';
import type { ProcessesResponse } from 'types';
import type { Bucket, EntityFilter, SortDirection, SortKey } from './Admin.consts';
import { BUCKET_STATUS, DEFAULT_DATA, defaultParams, SEARCH_DEBOUNCE_MS } from './Admin.consts';
import { formatValue, isCompany, matchesSearch, sortRows, toBucket, toServerParams } from './Admin.helpers';

export type AdminHook = ReturnType<typeof useAdmin>;

export function useAdmin() {
	const ctx = useContext(AppContext);
	const [search, setSearch] = useState('');
	const [initiatorKennitala, setInitiatorKennitala] = useState('');
	const [debouncedSearch, setDebouncedSearch] = useState('');
	const [debouncedInitiatorKennitala, setDebouncedInitiatorKennitala] = useState('');

	useEffect(() => {
		const id = setTimeout(() => {
			setDebouncedSearch(search);
			setDebouncedInitiatorKennitala(initiatorKennitala);
		}, SEARCH_DEBOUNCE_MS);
		return () => clearTimeout(id);
	}, [search, initiatorKennitala]);

	const url = useMemo(
		() =>
			ctx.config.API.getProcesses +
			queryString({
				...defaultParams,
				...toServerParams(debouncedSearch),
				initiatorsKennitala: debouncedInitiatorKennitala.replaceAll(/\D/g, '')
			}),
		[ctx.config.API.getProcesses, debouncedSearch, debouncedInitiatorKennitala]
	);
	const { error, isLoading, ...swr } = useSWR<ProcessesResponse[]>(url);
	const [bucket, setBucket] = useState<Bucket>('all');
	const [statusFilter, setStatusFilter] = useState('');
	const [flowFilter, setFlowFilter] = useState('');
	const [entityFilter, setEntityFilter] = useState<EntityFilter>('');
	const [sortKey, setSortKey] = useState<SortKey>('updated');
	const [sortDirection, setSortDirection] = useState<SortDirection>('desc');
	const data = Array.isArray(swr.data) ? swr.data : DEFAULT_DATA;

	const counts = useMemo(() => {
		const next = { all: data.length, active: 0, done: 0, expired: 0, cancelled: 0 };
		data.forEach(row => {
			next[toBucket(row.processStatus)] += 1;
		});
		return next;
	}, [data]);

	const filteredData = useMemo(() => {
		const query = search.trim().toLowerCase();
		const digits = query.replaceAll(/\D/g, '');

		const filtered = data.filter(row => {
			if (bucket !== 'all' && toBucket(row.processStatus) !== bucket) return false;
			if (statusFilter && String(row.processStatus) !== statusFilter) return false;
			if (flowFilter && String(row.flowType) !== flowFilter) return false;
			if (entityFilter === 'company' && !isCompany(row.kennitala)) return false;
			if (entityFilter === 'person' && isCompany(row.kennitala)) return false;
			return matchesSearch(row, query, digits);
		});

		return sortRows(filtered, sortKey, sortDirection);
	}, [bucket, data, entityFilter, flowFilter, search, sortDirection, sortKey, statusFilter]);

	const flowOptions = useMemo(() => {
		const uniq = new Set(data.map(row => String(row.flowType)));
		return [...uniq].sort((a, b) => Number(a) - Number(b));
	}, [data]);

	const statusOptions = useMemo(() => {
		const uniq = new Set(data.map(row => String(row.processStatus)));
		return [...uniq].sort((a, b) => Number(a) - Number(b));
	}, [data]);

	const tabs = useMemo(
		() =>
			(Object.keys(BUCKET_STATUS) as Bucket[]).map(key => ({
				count: counts[key],
				isActive: key === bucket,
				key,
				label: ctx.t(BUCKET_STATUS[key])
			})),
		[bucket, counts, ctx]
	);

	const toggleSort = (key: SortKey) => {
		if (sortKey === key) {
			setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
			return;
		}
		setSortKey(key);
		setSortDirection('desc');
	};

	const arrowFor = (key: SortKey) => {
		if (sortKey !== key) return '↕';
		return sortDirection === 'asc' ? '↑' : '↓';
	};

	const [drawerRow, setDrawerRow] = useState<ProcessesResponse | null>(null);
	const openDrawer = (row: ProcessesResponse) => setDrawerRow(row);
	const closeDrawer = () => setDrawerRow(null);
	const refreshData = () => swr.mutate();

	useEffect(() => {
		swr.mutate();
	}, []); // eslint-disable-line react-hooks/exhaustive-deps

	const formatStatus = (status: number) =>
		formatValue({ processStatus: status } as ProcessesResponse, 'processStatus', ctx.t);
	const formatFlow = (flowType: string) => ctx.t(`flow-type-${flowType}`);

	return {
		...ctx,
		arrowFor,
		bucket,
		closeDrawer,
		drawerRow,
		openDrawer,
		refreshData,
		counts,
		data: filteredData,
		entityFilter,
		error,
		flowFilter,
		flowOptions,
		formatFlow,
		formatStatus,
		formatValue,
		initiatorKennitala,
		isLoading,
		onEntityFilter: (e: React.ChangeEvent<HTMLSelectElement>) =>
			setEntityFilter(e.target.value as EntityFilter),
		onFlowFilter: (e: React.ChangeEvent<HTMLSelectElement>) => setFlowFilter(e.target.value),
		onInitiatorKennitala: (e: React.ChangeEvent<HTMLInputElement>) => setInitiatorKennitala(e.target.value),
		onSearch: (e: React.ChangeEvent<HTMLInputElement>) => setSearch(e.target.value),
		onStatusFilter: (e: React.ChangeEvent<HTMLSelectElement>) => setStatusFilter(e.target.value),
		search,
		setBucket,
		statusFilter,
		statusOptions,
		sortDirection,
		sortKey,
		tabs,
		toggleSort,
		totalCount: data.length
	};
}
